import { CtaButton } from '@deckee/api-app-main';
import { Box, Button, MessageBlock } from '@deckee/deck-hand';
import React, { FC, MouseEvent, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { selectCurrentMember } from '../../../../services/store/modules/backend-service';
import { callEndpoint, telemetry } from '../../../../services/backend';
import { captureEvent } from '../../../../services/event-capture';
import { hasFeatureEnabled } from '../../../../services/feature';
import { openWithInAppBrowser } from '../../../../services/in-app-browser';
import {
  ROUTE_MARKER_FLAG,
  ROUTE_STORY_MARKER_EDIT,
} from '../../../../services/route/route-names';
import { transitionToNextRoute } from '../../../../services/route/route-navigation';
import { useAppDispatch } from '../../../../services/store';
import {
  claimBusinessListing,
  resetMarketplaceSelectedListing,
} from '../../../../services/store/modules/marketplace';
import BasicDrawerLayout from '../../../layouts/BasicDrawerLayout/BasicDrawerLayout';
import HeaderBar from '../../../layouts/components/HeaderBar/HeaderBar';
import HeaderBarButton from '../../../layouts/components/HeaderBarButton/HeaderBarButton';
import LogoSpinner from '../../../shared/_deprecated/components/LogoSpinner/LogoSpinner';
import { back, pencil } from '../../../shared/vector-images';
import MarketplaceListingCard from '../components/MarketplaceListingCard/MarketplaceListingCard';
import useDistanceFormatting from '../hooks/use-distance-formatting';
import useSelectedListing from '../hooks/use-selected-listing';

const computeMarkerAllowEditing = (
  item: Record<string, unknown> | null | undefined,
  markerType: string,
  currentMember: { id: string | number } | null | undefined,
) => {
  if (!item) {
    return false;
  }

  let allowEditing = false;

  if (markerType === 'storyMarker' && item.owningInfluencer) {
    const influencer = item.owningInfluencer as { id: string | number };
    allowEditing = !!(
      currentMember && String(currentMember.id) === String(influencer.id)
    );
  }

  const flaggedAsDuplicate = item.flaggedAsDuplicate as boolean | undefined;
  const flaggedAsIncorrect = item.flaggedAsIncorrect as boolean | undefined;
  const editable = item.editable as boolean | undefined;

  if (editable && !flaggedAsDuplicate && !flaggedAsIncorrect) {
    allowEditing = true;
  }

  return allowEditing;
};

const MarketplaceListingPage: FC = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const currentMember = useSelector(selectCurrentMember);

  const [selectedListing, loadingListing, error] = useSelectedListing(id);
  const [businessClaimed, setBusinessClaimed] = useState(false);
  const [claimingListing, setClaimingListing] = useState(false);
  const [claimListingError, setClaimListingError] = useState<boolean>(false);
  const [markerAllowEditing, setMarkerAllowEditing] = useState(false);

  const {
    distanceFromRequestLocation,
    name,
    summary,
    coverImage,
    partner,
    ctaButtons,
    starRating,
    ratingsTotal,
  } = selectedListing || {};

  useEffect(
    () => () => {
      dispatch(resetMarketplaceSelectedListing());
    },
    [dispatch],
  );

  useEffect(() => {
    let cancelled = false;

    const loadMarkerEditEligibility = async () => {
      if (!selectedListing?.isMarker || !selectedListing.id) {
        setMarkerAllowEditing(false);
        return;
      }

      try {
        const item = await callEndpoint(
          callEndpoint.endpoints.mapSearchQueryById,
          { id: selectedListing.id },
        );

        if (cancelled) {
          return;
        }

        if (!item || (item as { hidden?: boolean }).hidden) {
          setMarkerAllowEditing(false);
          return;
        }

        setMarkerAllowEditing(
          computeMarkerAllowEditing(
            item as Record<string, unknown>,
            selectedListing.markerType,
            currentMember,
          ),
        );
      } catch (err) {
        if (!cancelled) {
          telemetry(
            telemetry.ERROR,
            `[MarketplaceListingPage.loadMarkerEditEligibility] Error loading marker edit eligibility ${JSON.stringify(
              err,
            )}`,
          );
          setMarkerAllowEditing(false);
        }
      }
    };

    loadMarkerEditEligibility();

    return () => {
      cancelled = true;
    };
  }, [selectedListing, currentMember]);

  const formatDistance = useDistanceFormatting();

  const handleEditMarker = useCallback(() => {
    if (!selectedListing?.isMarker) {
      return;
    }

    captureEvent({
      type: 'marker: edit',
      payload: {
        markerType: selectedListing.markerType,
        id: selectedListing.id,
      },
    });

    if (selectedListing.markerType !== 'storyMarker') {
      transitionToNextRoute(ROUTE_MARKER_FLAG, {
        markerType: selectedListing.markerType,
        id: selectedListing.id,
      });
    } else {
      transitionToNextRoute(ROUTE_STORY_MARKER_EDIT, {
        id: selectedListing.id,
      });
    }
  }, [selectedListing]);

  const handleCtaButtonClick = async (
    ctaButton: CtaButton,
    e: MouseEvent<HTMLElement>,
  ) => {
    captureEvent({
      type: 'marketplace: cta button click',
      payload: ctaButton,
      sendToAmplitude: true,
    });

    const { url } = ctaButton;

    // Check that the url is not a 'tel:' link as in-app browser will error
    if (url.indexOf('tel:') >= 0) return;

    try {
      e.preventDefault();
      e.stopPropagation();
      await openWithInAppBrowser({ url });
    } catch (err) {
      // Something has gone wrong, just navigate as usual
    }
  };

  const handleViewOnMap = () => {
    captureEvent({
      type: 'marketplace: view listing on map',
      payload: selectedListing,
      sendToAmplitude: true,
    });

    if (selectedListing) {
      history.push(`/maps/marketplace/listing/${selectedListing.id}/summary`);
    }
  };

  const handleClaimProfile = async () => {
    captureEvent({
      type: 'marketplace: claim listing',
      payload: selectedListing,
      sendToAmplitude: true,
    });

    if (selectedListing) {
      setClaimListingError(false); // reset claim error
      setClaimingListing(true);

      try {
        await dispatch(claimBusinessListing(selectedListing.id)).unwrap();
        setBusinessClaimed(true);
      } catch (e: any) {
        setClaimListingError(true);
      }

      setClaimingListing(false);
    }
  };

  const listingHeaderBar = (
    <HeaderBar
      solidBackground
      centerNode={name || ''}
      leftNode={
        <HeaderBarButton
          ariaLabel="return"
          icon={back()}
          onClick={() => history.push('/maps/marketplace/search')}
        />
      }
      rightNode={
        markerAllowEditing ? (
          <HeaderBarButton
            ariaLabel="edit"
            icon={pencil()}
            onClick={handleEditMarker}
          />
        ) : null
      }
    />
  );

  return (
    <BasicDrawerLayout
      onBack={() => history.push('/maps/marketplace/search')}
      onClose={null}
      renderHeaderBar={!loadingListing ? listingHeaderBar : undefined}
    >
      <Box p="3" data-testid="business-listing-details-page">
        {error && (
          <MessageBlock type="error" mb="3">
            {error}
          </MessageBlock>
        )}

        {loadingListing ? (
          <LogoSpinner />
        ) : (
          <>
            <MarketplaceListingCard
              name={name || ''}
              coverImage={coverImage}
              summary={summary}
              distance={
                (distanceFromRequestLocation &&
                  formatDistance(distanceFromRequestLocation)) ||
                ''
              }
              onViewOnMap={handleViewOnMap}
              partner={!!partner}
              ctaButtons={ctaButtons || []}
              onCtaButtonClick={handleCtaButtonClick}
              starRating={starRating}
              ratingsTotal={ratingsTotal || undefined}
            />

            {claimListingError && (
              <MessageBlock type="error" mt="3">
                Sorry, an unexpected error occurred! Please try again.
              </MessageBlock>
            )}
            {!claimListingError && businessClaimed && (
              <MessageBlock type="success" mt="3">
                Great! We just sent you an email with next steps to help you
                reach more customers on Deckee.
              </MessageBlock>
            )}
            {claimingListing && <LogoSpinner />}
            {!partner &&
              !businessClaimed &&
              !claimingListing &&
              hasFeatureEnabled('marketplace-claim-profile') && (
                <Box textAlign="center" mt={4}>
                  <Box mb={2}>Do you own this business?</Box>
                  <Button link onClick={handleClaimProfile}>
                    Enhance your profile
                  </Button>
                </Box>
              )}
          </>
        )}
      </Box>
    </BasicDrawerLayout>
  );
};

export default MarketplaceListingPage;
